// Employee self-service: own regularisation requests with decision history.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, X, Eye, CalendarCheck, Paperclip, PaperPlaneTilt, PencilSimple } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "../../lib/format";
import { REQ_STATUS_CLS, TYPE_LABEL, openEvidence, dayHoursLabel } from "../../lib/regularisation";
import { RequestHistory } from "../../components/regularisation/RequestHistory";

function Outcome({ r }) {
    const o = r.final_outcome;
    if (!o) return null;
    return (
        <div className={`rounded-md border p-3 text-xs ${o.decision === "rejected" ? "border-rose-200 bg-rose-50 text-rose-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`} data-testid="my-rgz-outcome">
            <div className="font-semibold">{o.decision === "modified" ? "Approved with changes" : o.decision === "rejected" ? "Rejected" : "Approved"} by {o.decided_by} · {formatDateTime(o.decided_at)}</div>
            <div>Final attendance: <b>{o.final_status || "—"}</b>{(o.net_working_minutes || o.credited_minutes) ? ` · ${dayHoursLabel(o.net_working_minutes, o.credited_minutes)}` : ""}</div>
            {o.reason && <div className="italic mt-0.5">&ldquo;{o.reason}&rdquo;</div>}
        </div>
    );
}

function DetailBody({ d, onWithdraw, onSubmitDraft, onEditDraft, onOpenMonth }) {
    const rc = d.requested_correction || {};
    return (
        <div className="space-y-3 text-sm">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1">
                <div><b>Reason:</b> {d.reason_label || "—"}</div>
                <div><b>Explanation:</b> {d.justification || "—"}</div>
                {rc.check_in_time && <div><b>Requested check-in:</b> {formatDateTime(rc.check_in_time)}</div>}
                {rc.check_out_time && <div><b>Requested check-out:</b> {formatDateTime(rc.check_out_time)}</div>}
                {d.pending_with && <div><b>Pending with:</b> {d.status === "Draft" ? "You (draft)" : d.pending_with}</div>}
                {d.submitted_after_deadline && <Badge className="bg-orange-100 text-orange-800 text-[10px]">Submitted after the {formatDate(d.employee_deadline)} deadline</Badge>}
                {d.evidence_object_id && (
                    <button type="button" className="text-indigo-700 hover:underline flex items-center gap-1" onClick={() => openEvidence(d.evidence_object_id).catch((e) => toast.error(formatApiError(e, "Cannot open evidence")))} data-testid="my-rgz-evidence-open">
                        <Paperclip size={12} /> {d.evidence_filename || "Evidence"}
                    </button>
                )}
            </div>
            <Outcome r={d} />
            <RequestHistory request={d} />
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                {["Pending", "Draft"].includes(d.status) && (
                    <Button variant="outline" onClick={onWithdraw} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="my-rgz-withdraw"><X size={14} className="mr-1" /> Withdraw</Button>
                )}
                {d.status === "Draft" && <Button variant="outline" onClick={onEditDraft} data-testid="my-rgz-edit-draft"><PencilSimple size={14} className="mr-1" /> Edit draft</Button>}
                {d.status === "Draft" && <Button onClick={onSubmitDraft} data-testid="my-rgz-submit-draft"><PaperPlaneTilt size={14} className="mr-1" /> Submit</Button>}
                <Button variant="outline" onClick={onOpenMonth} data-testid="my-rgz-open-month"><CalendarCheck size={14} className="mr-1" /> Open month</Button>
            </div>
        </div>
    );
}

export default function MyRegularizationRequests() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [detail, setDetail] = useState(null);
    const [linked, setLinked] = useState(true);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/regularization/request/list", { params: { scope: "mine", status_filter: "all", page_size: 200 } });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            if (err?.response?.status === 403) { setLinked(false); return; }
            toast.error(formatApiError(err, "Failed to load your requests"));
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const open = useCallback(async (id) => {
        try {
            const { data } = await api.get(`/regularization/request/${id}`);
            setDetail(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load request"));
        }
    }, []);
    useEffect(() => { const id = params.get("id"); if (id) open(id); }, [params, open]);

    const act = async (path, msg) => {
        try {
            await api.post(`/regularization/request/${detail.id}/${path}`);
            toast.success(msg);
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Action failed"));
        }
    };

    if (!linked) {
        return <div className="max-w-3xl" data-testid="my-rgz-unlinked"><PageHeader eyebrow="My Regularisations" title="Account not linked" description="Ask HR to link your account to your employee record." /></div>;
    }

    return (
        <div className="max-w-[1200px]" data-testid="my-rgz-page">
            <PageHeader
                eyebrow="My Attendance & Regularisation"
                title="My requests"
                description="Every request keeps its own decision and full history. Pending and draft requests can be withdrawn."
                actions={<>
                    <Button variant="outline" onClick={() => navigate("/attendance/my-month")} data-testid="my-rgz-month-btn"><CalendarCheck size={14} className="mr-1" /> My month</Button>
                    <Button onClick={() => navigate("/regularization/new")} data-testid="my-rgz-new-btn"><Plus size={14} className="mr-1" /> New request</Button>
                </>}
            />
            <section className="m-6 sm:m-8 bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto" data-testid="my-rgz-section">
                <div className="flex justify-between items-center mb-3 text-xs text-slate-500">
                    <span>{total} request(s)</span><span className="uppercase tracking-wider text-[10px]">All times in IST</span>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            {["Status", "Date", "Request", "Reason", "Pending with", "Submitted", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No requests yet.</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => open(r.id)} data-testid={`my-rgz-row-${r.id}`}>
                                <td className="px-3 py-2"><Badge className={`text-[10px] ${REQ_STATUS_CLS[r.status]}`}>{r.final_outcome?.decision === "modified" ? "Approved (changed)" : r.status}</Badge></td>
                                <td className="px-3 py-2 text-xs font-mono">{formatDate(r.attendance_date)}</td>
                                <td className="px-3 py-2 text-xs">{TYPE_LABEL[r.request_type] || r.request_type}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{r.reason_label || "—"}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{r.pending_with || "—"}</td>
                                <td className="px-3 py-2 text-xs text-slate-500">{r.submitted_at ? formatDateTime(r.submitted_at) : "—"}</td>
                                <td className="px-3 py-2 text-right"><Eye size={14} className="inline text-slate-400" /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
            <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
                <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="my-rgz-detail-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    {TYPE_LABEL[detail.request_type] || detail.request_type}
                                    <Badge className={`text-[10px] ${REQ_STATUS_CLS[detail.status]}`}>{detail.status}</Badge>
                                </DialogTitle>
                                <DialogDescription>{detail.id} · {formatDate(detail.attendance_date)}</DialogDescription>
                            </DialogHeader>
                            <DetailBody
                                d={detail}
                                onWithdraw={() => window.confirm("Withdraw this request?") && act("withdraw", "Request withdrawn.")}
                                onSubmitDraft={() => act("submit", "Draft submitted for approval.")}
                                onEditDraft={() => navigate(`/regularization/new?draft=${encodeURIComponent(detail.id)}`)}
                                onOpenMonth={() => navigate(`/attendance/my-month?period=${detail.attendance_date.slice(0, 7)}`)}
                            />
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
